import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthContext } from '../contexts/AuthContext'

/**
 * OAuth callback handler component
 * This component handles the redirect from Cognito Hosted UI after authentication
 */
export default function AuthCallback() {
  const navigate = useNavigate()
  const { isAuthenticated, isLoading, refreshSession } = useAuthContext()
  const [processing, setProcessing] = useState(false)

  useEffect(() => {
    const handleCallback = async () => {
      if (processing) return
      
      console.log('[AuthCallback] Starting callback handler', { isAuthenticated, isLoading })
      console.log('[AuthCallback] Current URL:', window.location.href)
      
      // Check if we have a code in the URL
      const urlParams = new URLSearchParams(window.location.search)
      const code = urlParams.get('code')
      
      if (code) {
        console.log('[AuthCallback] Found OAuth code, exchanging for tokens...')
        setProcessing(true)
        
        try {
          // Exchange code for tokens manually
          const cognitoDomain = import.meta.env.VITE_COGNITO_DOMAIN
          const clientId = import.meta.env.VITE_USER_POOL_CLIENT_ID
          const redirectUri = import.meta.env.VITE_CALLBACK_URL
          
          const tokenEndpoint = `${cognitoDomain}/oauth2/token`
          
          const body = new URLSearchParams({
            grant_type: 'authorization_code',
            client_id: clientId,
            code: code,
            redirect_uri: redirectUri
          })
          
          console.log('[AuthCallback] Calling token endpoint:', tokenEndpoint)
          
          const response = await fetch(tokenEndpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded'
            },
            body: body.toString()
          })
          
          if (!response.ok) {
            const errorText = await response.text()
            console.error('[AuthCallback] Token exchange failed:', response.status, errorText)
            throw new Error(`Token exchange failed: ${response.status}`)
          }
          
          const tokens = await response.json()
          console.log('[AuthCallback] Got tokens, storing in Amplify...')
          
          // Store tokens in localStorage for Amplify to pick up
          const userPoolId = import.meta.env.VITE_USER_POOL_ID
          const lastAuthUserKey = `CognitoIdentityServiceProvider.${clientId}.LastAuthUser`
          const username = JSON.parse(atob(tokens.id_token.split('.')[1])).sub
          
          localStorage.setItem(lastAuthUserKey, username)
          localStorage.setItem(`CognitoIdentityServiceProvider.${clientId}.${username}.idToken`, tokens.id_token)
          localStorage.setItem(`CognitoIdentityServiceProvider.${clientId}.${username}.accessToken`, tokens.access_token)
          localStorage.setItem(`CognitoIdentityServiceProvider.${clientId}.${username}.refreshToken`, tokens.refresh_token)
          
          console.log('[AuthCallback] Tokens stored, refreshing session...')
          
          // Clear the code from URL
          window.history.replaceState({}, document.title, '/auth')
          
          // Refresh session to pick up the new tokens
          await refreshSession()
          
          setTimeout(() => {
            console.log('[AuthCallback] Redirecting to dashboard')
            navigate('/dashboard', { replace: true })
          }, 500)
          
        } catch (error) {
          console.error('[AuthCallback] Error exchanging code:', error)
          setProcessing(false)
          navigate('/login', { replace: true })
        }
        return
      }
      
      // No code, check if already authenticated
      if (isAuthenticated) {
        console.log('[AuthCallback] Already authenticated, redirecting to dashboard')
        navigate('/dashboard', { replace: true })
      } else {
        console.log('[AuthCallback] No code and not authenticated, redirecting to login')
        navigate('/login', { replace: true })
      }
    }

    if (!isLoading && !processing) {
      console.log('[AuthCallback] Not loading, calling handleCallback')
      handleCallback()
    } else {
      console.log('[AuthCallback] Still loading or processing, waiting...')
    }
  }, [isAuthenticated, isLoading, navigate, refreshSession, processing])

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Completing sign in...</p>
      </div>
    </div>
  )
}
